/**
 * Escapes a value for safe inclusion in a CSV field per RFC 4180.
 * Converts non-string values to their string representation; null/undefined become empty.
 * @param value - The value to escape
 * @returns The CSV-safe string representation
 */
export function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const str = stringifyCsvValue(value);
  if (
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function stringifyCsvValue(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "bigint") {
    return String(value);
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value) ?? "";
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Converts an array of row objects to a CSV string with a header row.
 * @param columns - Column names for the header row
 * @param rows - Array of row objects keyed by column name
 * @returns CSV-formatted string
 */
export function rowsToCsv(
  columns: string[],
  rows: Record<string, unknown>[],
): string {
  const header = columns.map(escapeCsvField).join(",");
  const dataLines = rows.map((row) =>
    columns.map((col) => escapeCsvField(row[col])).join(","),
  );
  return [header, ...dataLines].join("\n");
}

/**
 * Parses a CSV string (with header row) into an array of row objects.
 * Handles quoted fields with commas, escaped double-quotes, and newlines per RFC 4180.
 * @param csv - The CSV string to parse
 * @returns An object with columns and rows
 */
export function parseCsv(csv: string): {
  columns: string[];
  rows: Array<Record<string, string>>;
} {
  const lines = splitCsvLines(csv);
  if (lines.length === 0) {
    return { columns: [], rows: [] };
  }

  const columns = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    const row: Record<string, string> = {};
    for (let i = 0; i < columns.length; i++) {
      row[columns[i]] = values[i] ?? "";
    }
    return row;
  });

  return { columns, rows };
}

const MAX_SAMPLE_ROWS = 10;

/**
 * Parses a CSV string and returns up to 10 sample data rows (excluding the header).
 * Handles RFC 4180 quoting (quoted fields with commas, newlines, and escaped double-quotes).
 * @param csv - The full CSV string with header row followed by data rows
 * @returns Array of row arrays, each containing cell values as strings
 */
export function parseCsvSampleRows(csv: string): string[][] {
  const rows = parseCsvRows(csv);
  return rows.slice(1, MAX_SAMPLE_ROWS + 1);
}

/**
 * Splits a CSV string into logical lines, respecting quoted fields that contain newlines.
 * @param csv - The full CSV string
 * @returns Array of logical CSV lines
 */
function splitCsvLines(csv: string): string[] {
  const lines: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
    } else if (char === "\n" && !inQuotes) {
      if (current.length > 0) {
        lines.push(current);
      }
      current = "";
    } else if (char === "\r" && !inQuotes) {
      continue;
    } else {
      current += char;
    }
  }

  if (current.length > 0) {
    lines.push(current);
  }

  return lines;
}

/**
 * Parses a single CSV line into field values, handling quoted fields.
 * @param line - A single logical CSV line
 * @returns Array of field values
 */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  fields.push(current);
  return fields;
}

/**
 * Parses a CSV string into an array of row arrays, handling RFC 4180 quoting.
 * Stops parsing early once enough rows are collected for sampling.
 * @param csv - The CSV string to parse
 * @returns Array of row arrays
 */
function parseCsvRows(csv: string): string[][] {
  const rows: string[][] = [];
  let current = 0;

  while (current < csv.length) {
    const { fields, nextIndex } = parseRow(csv, current);
    rows.push(fields);
    current = nextIndex;

    if (rows.length > MAX_SAMPLE_ROWS + 1) {
      break;
    }
  }

  return rows;
}

/**
 * Parses a single CSV row starting at the given index.
 * @param csv - The full CSV string
 * @param start - The index to start parsing from
 * @returns The parsed fields and the index after the row terminator
 */
function parseRow(
  csv: string,
  start: number,
): { fields: string[]; nextIndex: number } {
  const fields: string[] = [];
  let i = start;

  while (i < csv.length) {
    if (csv[i] === '"') {
      const { value, nextIndex } = parseQuotedField(csv, i);
      fields.push(value);
      i = nextIndex;
    } else {
      const nextComma = csv.indexOf(",", i);
      const nextNewline = csv.indexOf("\n", i);
      const nextDelimiter =
        nextComma === -1
          ? nextNewline
          : nextNewline === -1
            ? nextComma
            : Math.min(nextComma, nextNewline);

      if (nextDelimiter === -1) {
        fields.push(csv.slice(i));
        return { fields, nextIndex: csv.length };
      }

      fields.push(csv.slice(i, nextDelimiter));

      if (csv[nextDelimiter] === "\n") {
        return { fields, nextIndex: nextDelimiter + 1 };
      }

      i = nextDelimiter + 1;

      if (i >= csv.length) {
        fields.push("");
        return { fields, nextIndex: i };
      }
    }

    if (i <= csv.length && csv[i - 1] === "\n") {
      return { fields, nextIndex: i };
    }
  }

  return { fields, nextIndex: i };
}

/**
 * Parses a quoted CSV field starting at the opening double-quote.
 * Handles escaped double-quotes ("") within the field.
 * @param csv - The full CSV string
 * @param start - The index of the opening double-quote
 * @returns The unescaped field value and the index after the closing quote and delimiter
 */
function parseQuotedField(
  csv: string,
  start: number,
): { value: string; nextIndex: number } {
  let i = start + 1;
  let value = "";

  while (i < csv.length) {
    if (csv[i] === '"') {
      if (i + 1 < csv.length && csv[i + 1] === '"') {
        value += '"';
        i += 2;
      } else {
        i += 1;
        if (i < csv.length && csv[i] === ",") {
          return { value, nextIndex: i + 1 };
        }
        if (i < csv.length && csv[i] === "\n") {
          return { value, nextIndex: i + 1 };
        }
        return { value, nextIndex: i };
      }
    } else {
      value += csv[i];
      i += 1;
    }
  }

  return { value, nextIndex: i };
}
